import { cx } from "@/utils/cx";
import {
  eurIsPositive,
  formatEurDashboard,
  formatKcDashboard,
  type SpzBarPoint,
} from "../../schemas/tenantDashboard";

/**
 * UC-005 SpzMobileBreakdownTable — fallback per ANALYZE §5.5.1 varianta c
 * (Phase 23, REQ-UI-014 + REQ-UI-015 + REQ-UI-018).
 *
 * Renderuje per-SPZ rozklad pod TimelineChart na mobile (< md). Multi-line
 * chart by byl nečitelný na 412×… viewportu, takže jeho roli supluje tato
 * tabulka — strukturovaná data, sortable klávesnicí přes browser native řazení
 * tabulky.
 *
 * Vždy renderuje na mobile, na desktopu lze schovat pomocí `hideOnDesktop`
 * (default = true). Volající stránka může nahradit za vlastní variantu.
 */

export interface SpzMobileBreakdownTableProps {
  points: SpzBarPoint[];
  hideOnDesktop?: boolean;
  /** Pokud TRUE → zobrazí EUR sloupec (pouze pokud sum EUR > 0). */
  showEur?: boolean;
}

export function SpzMobileBreakdownTable({
  points,
  hideOnDesktop = true,
  showEur = false,
}: SpzMobileBreakdownTableProps) {
  const wrapperClassName = cx("mt-3", hideOnDesktop && "md:hidden");

  const hasEurColumn = showEur && points.some((p) => eurIsPositive(p.total_eur));

  const thClass =
    "border-b border-secondary bg-secondary px-4 py-3 text-left text-xs font-semibold tracking-[0.05em] text-tertiary uppercase";
  const tdClass = "border-b border-secondary px-4 py-3 text-left";

  if (points.length === 0) {
    return (
      <div className={wrapperClassName} data-testid="spz-mobile-breakdown">
        <p className="m-0 text-tertiary">Žádné SPZ v zvoleném období.</p>
      </div>
    );
  }

  return (
    <div className={wrapperClassName} data-testid="spz-mobile-breakdown">
      <table className="w-full border-collapse text-[0.9375rem] text-primary">
        <caption className="sr-only">
          Per-SPZ rozklad za zvolené období.
        </caption>
        <thead>
          <tr>
            <th scope="col" className={thClass}>SPZ</th>
            <th scope="col" className={thClass}>CZK</th>
            {hasEurColumn && <th scope="col" className={thClass}>EUR</th>}
            <th scope="col" className={thClass}>Transakce</th>
          </tr>
        </thead>
        <tbody>
          {points.map((p) => (
            <tr
              key={p.fuel_card_id}
              data-testid={`spz-mobile-row-${p.fuel_card_id}`}
              className="hover:bg-secondary"
            >
              <td className={tdClass}>
                <strong>{p.plate_text}</strong>
              </td>
              <td className={tdClass}>{formatKcDashboard(p.total_kc)}</td>
              {hasEurColumn && (
                <td className={tdClass}>
                  {eurIsPositive(p.total_eur) ? formatEurDashboard(p.total_eur) : "—"}
                </td>
              )}
              <td className={tdClass}>{p.transaction_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
